import axios, { AxiosRequestConfig } from "axios"
import { API_BASE } from "./config"

axios.defaults.xsrfCookieName = "csrftoken"
axios.defaults.xsrfHeaderName = "X-CSRFToken"

const csrf_url = `${API_BASE}/csrf/`
const token_url = `${API_BASE}/token/get_auth_token/`

type AxiosHeader = {
    "Content-Type"?: string;
    Authorization?: string;
    "X-CSRFToken"?: string;
};

type OrderedJob = {
    id?: string | number;
    applied?: boolean;
    has_email?: boolean;
    job_id: number;
    job_score?: number;
    processed?: boolean;
    relevant?: boolean;
    salary_max?: number;
    salary_max_currency?: string;
    salary_min?: number;
    salary_min_currency?: string;
};

type RankedJob = {
    id: number;
    formatted_post_date?: string;
    post_date?: string;
    jobtitle?: string;
    organization?: string;
    location?: string;
    salary_minimum?: number;
    salary_maximum?: number;
    salary_currency?: string;
    salary_period?: string;
    salary_display?: string;
    salary_is_from_description?: boolean;
    salary_source_label?: string | null;
    url?: string;
    jobdesc?: string;
    jobdesc_html?: string;
    email?: boolean;
    url_text?: string;
    unique_id?: string;
    datasource?: string;
    slug?: string;
    status?: string;
};

async function assign_job_to_user(
    job: OrderedJob | RankedJob,
    method: string = "post",
) {
    const headers: AxiosHeader = await get_headers(true)
    const id_url = `${API_BASE}/whoami/`
    const id_resp = await make_api_call(id_url, "get", {}, headers)

    const user_id = id_resp.user_id

    const payload: { [k: string]: any } = {}

    let url = `${API_BASE}/job_matches/`

    if ('relevant' in job) {
        payload.relevant = job.relevant
        url = `${url}${job.id}/`
    } else {
        payload.user_id = user_id
        payload.job_id = job.id
    }

    const resp = await make_api_call(url, method, {}, headers, payload)

    return resp
}

async function get_csrf_cookie() {
    return axios
        .get(csrf_url, {
            withCredentials: true,
        })
        .then((resp) => resp.headers["x-csrftoken"])
}

async function get_user_token() {
    const csrf_cookie = await get_csrf_cookie()
    const headers: AxiosHeader = {
        "X-CSRFToken": csrf_cookie,
    }
    const req = axios.get(token_url, { headers })
    return req.then((resp) => resp.data.detail.user_token)
}

async function get_headers(auth: boolean = false) {
    const headers: AxiosHeader = {
        "Content-Type": "application/json",
    }
    if (auth !== false) {
        const bearer_token = await get_user_token()
        headers["Authorization"] = `Token ${bearer_token}`
    }
    headers["X-CSRFToken"] = await get_csrf_cookie()
    return headers
}

async function make_api_call(
    url: string,
    method: string = "get",
    params?: Record<string, object[] | any>,
    headers?: AxiosHeader,
    data_payload?: Record<string, object[] | any>,
) {
    const reqConfig: AxiosRequestConfig = {
        method: method,
        url: url,
        headers: headers,
        params: params,
        data: data_payload,
    }

    const resp = await axios
        .request(reqConfig)
        .then((resp) => resp.data)
        .catch(function (err) {
            // Error handled silently - API failures return undefined
        })
    return resp
}

function get_title_str(title: string) {
    const title_parts = title.split("_")
    const title_case_parts = title_parts.map(
        (w: string) => w[0].toUpperCase() + w.substr(1).toLowerCase(),
    )
    title = title_case_parts.join(" ")

    return title
}

// Matches any block-level HTML element — signals the content is already HTML-formatted.
const BLOCK_HTML_RE = /<(p|div|ul|ol|li|h[1-6]|br|table|blockquote|section|article)\b/i

// Minimum text length (stripped of tags) for a <p> to be treated as a "wall paragraph"
// requiring normalisation.
const WALL_PARA_THRESHOLD = 300

/**
 * Inject line breaks into a single "wall" paragraph whose raw text has no
 * internal block structure.  Handles four source patterns seen in the API:
 *
 *   • ALL-CAPS section labels  — SKILLS REQUIRED:, EMPLOYER NAME:, etc.
 *   • <strong>Header</strong>text  — bold section header followed directly by text
 *   • - Item  — dash-bullet items (CareerOneStop / DOL style)
 *   • * Item  — asterisk-bullet items
 *   • •Item   — U+2022 bullet used as inline separator
 */
function normaliseWallParagraph(inner: string): string {
    let s = inner

    // Break after </strong> when text or open-paren follows directly
    s = s.replace(/<\/strong>(?=[A-Za-z(])/g, "</strong><br>")

    // Break before ALL-CAPS section labels (≥5 chars before the colon)
    s = s.replace(/([A-Z][A-Z\s]{3,}:)/g, "<br><strong>$1</strong>")

    // U+2022 bullet used as inline separator (•Item → <br>• Item)
    s = s.replace(/(?<=\S)•/g, "<br>•")

    // Dash-bullet items: "-   Item" or "-  Item"
    s = s.replace(/-\s{1,4}(?=[A-Z(])/g, "<br>- ")

    // Asterisk-bullet items: " * Item"
    s = s.replace(/\s+\*\s+(?=[A-Za-z(])/g, "<br>• ")

    return s
}

/**
 * Normalise a raw `jobdesc` string into renderable HTML.
 *
 * All 200 jobs in the API return block HTML (every entry has at least <p>
 * tags), so the plain-text path is a safety net for edge cases.
 *
 * The main problem is "wall" jobs: valid HTML where all content is crammed
 * into one or two <p> elements with no internal line breaks.  These come from
 * CareerOneStop, staffing agencies, and similar sources.  We post-process any
 * <p> whose plain-text content exceeds WALL_PARA_THRESHOLD chars.
 *
 * DOMPurify sanitisation is left to the call site so this function stays pure
 * and testable without a DOM.
 */
function formatJobDesc(raw: string): string {
    if (!raw) return ""

    if (!BLOCK_HTML_RE.test(raw)) {
        // Plain-text fallback: convert newline structure to HTML
        return raw
            .split(/\n{2,}/)
            .map((para) => {
                const lines = para.split("\n").map((l) => l.trim()).filter(Boolean)
                if (!lines.length) return ""
                const isList = lines.length > 1 && lines.every((l) => /^[*\-•·]/.test(l))
                if (isList) {
                    const items = lines
                        .map((l) => `<li>${l.replace(/^[*\-•·]\s*/, "")}</li>`)
                        .join("")
                    return `<ul>${items}</ul>`
                }
                return `<p>${lines.join("<br>")}</p>`
            })
            .filter(Boolean)
            .join("")
    }

    // Block HTML: normalise wall paragraphs only
    return raw.replace(/<p>([\s\S]*?)<\/p>/gi, (match, inner) => {
        const textLen = inner.replace(/<[^>]+>/g, "").trim().length
        return textLen >= WALL_PARA_THRESHOLD
            ? `<p>${normaliseWallParagraph(inner)}</p>`
            : match
    })
}

export { assign_job_to_user, get_headers, make_api_call, get_title_str, formatJobDesc }
